'use client';

import { useState, useTransition } from 'react';
import { Heart, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import type { SaveButtonProps } from '../types';

export default function SaveButton({
  reviewId,
  initialSavedStatus,
  onSaveToggle,
  disabled = false,
  currentUser
}: SaveButtonProps) {
  const isAuthenticated = currentUser?.isAuthenticated || false;
  const userId = currentUser?.userId;
  const [isSaved, setIsSaved] = useState(initialSavedStatus.isSaved);
  const [isPending, startTransition] = useTransition();

  const handleSaveToggle = async () => {
    if (!isAuthenticated) {
      toast.error('Please log in to save properties');
      return;
    }

    if (disabled || isPending) return;

    // Optimistic update
    const newSavedState = !isSaved;
    setIsSaved(newSavedState);

    startTransition(async () => {
      try {
        const response = await fetch('/api/saved-properties', {
          method: newSavedState ? 'POST' : 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            reviewId,
            userId
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to update saved status');
        }

        const result = await response.json();
        
        // Call optional callback
        onSaveToggle?.(newSavedState);

        // Show success message
        toast.success(
          newSavedState 
            ? 'Property saved to your favorites' 
            : 'Property removed from favorites'
        );

      } catch (error) {
        // Revert optimistic update on error
        setIsSaved(!newSavedState);
        
        console.error('Error updating saved status:', error);
        toast.error('Failed to update saved status. Please try again.');
      }
    });
  };

  if (!isAuthenticated) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={handleSaveToggle}
        className="flex items-center gap-2"
      >
        <Heart className="h-4 w-4" />
        Save
      </Button>
    );
  }

  return (
    <Button
      variant={isSaved ? "default" : "outline"}
      size="sm"
      onClick={handleSaveToggle}
      disabled={disabled || isPending}
      className={cn(
        "flex items-center gap-2 transition-colors",
        isSaved && "bg-red-500 hover:bg-red-600 text-white"
      )}
    >
      {isPending ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <Heart 
          className={cn(
            "h-4 w-4 transition-colors",
            isSaved ? "fill-current" : ""
          )} 
        />
      )}
      
      <span className="hidden sm:inline">
        {isSaved ? 'Saved' : 'Save'}
      </span>
    </Button>
  );
} 